import React, {FC, useEffect, useMemo, useState} from 'react';
import Box from '@material-ui/core/Box';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import {Filter} from '@epic-ui/icons';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import LineChart from '../components/chart/LineChart';
import HorizontalLabel from '../components/HorizontalLabel';
import {Widget} from '../../../../common/widget';
import WidgetTooltip from '../components/WidgetTooltip';
import IntervalRadioGroup from '../components/interval-radio-group/IntervalRadioGroup';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import WidgetGameSelect from '../../../../game-select/WidgetGameSelect';
import WidgetDatePicker from '../../../../widget-date-picker/WidgetDatePicker';
import TimeScaleSelect from '../../../../time-scale-select/TimeScaleSelect';
import IconButton from '../../../../common/IconButton';

import useAveragePlayTimeData from './useAveragePlayTimeData';
import {LEGENDS_LIMIT, tooltipFormatters, xAxisFormatters} from '../constants';
import useChartTrends from '../hooks/useChartTrends';
import {useRangePickerData} from '../../../../../components/common/date-range-picker';
import {TIME_SCALES} from '../../../../constants';
import {prepareLineChartExportData} from '../exportUtils';
import {getWidgetTimeRangeDates} from '../utils';

import {WidgetProps} from '../types';
import {Status, DateRange, TimeScale} from '../../../../../types';

import {ChartWrapper, OptionWrapper} from '../styles';
import {Formatter} from '../../../../../utils/CSVUtils';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import useWidgetSelectedGames from '../hooks/useWidgetSelectedGames';
import useDurationFormatter from '../hooks/useDurationFormatter';

const CODES = {
    title: 'epic.affiliate.fortnite_games.average_play_time',
    tooltip: 'epic.affiliate.fortnite_games.average_play_time.tooltip',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    showMe: 'epic.affiliate.fortnite_games.show_me'
};

const csvValueFormatter: Formatter = (value, property) => {
    if (property === 'time' && !value) {
        return '';
    }

    const numericValue = +value;

    if (Number.isNaN(numericValue)) {
        return String(value);
    }

    const hours = Math.trunc(numericValue / 60);
    const minutes = numericValue % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const AveragePlayTimeWidget: FC<WidgetProps> = ({
    globalTimeScale,
    selectedDate: selected,
    startDate: start,
    endDate: end,
    selectedGames: selectedGamesProps,
    gamesList,
    getMessage,
    keysMap,
    gamesLoading
}: WidgetProps) => {
    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const isXlBreakpoint = useMediaQuery(theme.breakpoints.up('xl'));
    const formatDuration = useDurationFormatter();

    const timeScaleOptions = useMemo(() => {
        return TIME_SCALES.slice(1).map(value => ({
            label: getMessage(`${CODES.timeScale}${value}`),
            value
        }));
    }, []);

    const [timeScale, setTimeScale] = useState(timeScaleOptions[0]);
    const [isPreselectedRange, setIsPreselectedRange] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [selectedGames, handleSelectGames] = useWidgetSelectedGames(selectedGamesProps);

    const {selectedDate, startDate, endDate, setCalendar} = useRangePickerData(
        getWidgetTimeRangeDates(TimeScale.DAILY)
    );

    const {data, loading, error, reload} = useAveragePlayTimeData({
        startDate,
        endDate,
        timeScale: timeScale.value,
        selectedGames
    });

    const chartData = data[timeScale.value].list || [];
    const emptyDataKeys = data[timeScale.value].empty;

    const trends = useChartTrends({data: chartData, xAxis: 'time'});

    // Handlers
    const handleRangeChange = (dateRange: DateRange) => {
        setCalendar(dateRange);
        setIsPreselectedRange(false);
    };

    const handleTimeScaleChange = timeScale => {
        const timeRangeDates = getWidgetTimeRangeDates(timeScale.value);

        setTimeScale(timeScale);
        setCalendar(timeRangeDates);
        setIsPreselectedRange(false);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Effects
    useEffect(() => {
        const selectedOption = timeScaleOptions.find(({value}) => value === globalTimeScale);
        if (!isPreselectedRange) {
            setTimeScale(selectedOption);
        }

        if (!isPreselectedRange || (!isMobileMenuOpen && !isFmdBreakpoint)) {
            setCalendar({startDate: start, endDate: end, selectedDate: selected});
        }
    }, [start, end, selected]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    const GameSelect = (
        <WidgetGameSelect options={gamesList} value={selectedGames} onChange={handleSelectGames} />
    );

    const TimeSelect = (
        <TimeScaleSelect
            options={timeScaleOptions}
            value={timeScale}
            onChange={handleTimeScaleChange}
        />
    );

    const DatePicker = (
        <WidgetDatePicker
            timeScale={timeScale.value}
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleRangeChange}
        />
    );

    return (
        <PDFPictureTrap id={CODES.title} index={2}>
            <div>
                <Widget
                    height={530}
                    header={getMessage(CODES.title)}
                    padding="15px"
                    tooltip={<WidgetTooltip>{getMessage(CODES.tooltip)}</WidgetTooltip>}
                    status={error ? Status.ERROR : undefined}
                    message={getMessage(error) || error}
                    overflow="visible"
                    retry={reload}
                    actions={
                        <>
                            {isFmdBreakpoint ? (
                                <>
                                    {isXlBreakpoint ? (
                                        <HorizontalLabel>
                                            {getMessage(CODES.showMe)}
                                            <IntervalRadioGroup
                                                options={timeScaleOptions}
                                                value={timeScale}
                                                onChange={handleTimeScaleChange}
                                            />
                                        </HorizontalLabel>
                                    ) : (
                                        <Box mx={1.5} width={140}>
                                            {TimeSelect}
                                        </Box>
                                    )}

                                    <Box mx={1.5} width={233}>
                                        {DatePicker}
                                    </Box>
                                    <Box mx={1.5} width={178}>
                                        {GameSelect}
                                    </Box>
                                </>
                            ) : (
                                <IconButton
                                    onClick={handleMobileMenuClick}
                                    isOpen={isMobileMenuOpen}>
                                    <Filter />
                                </IconButton>
                            )}
                            <WidgetDownloadButton
                                areaId={CODES.title}
                                fileName={getMessage(CODES.title)}
                                data={chartData}
                                csvValueFormatter={csvValueFormatter}
                                prepareCSVData={prepareLineChartExportData(keysMap)}
                                disabled={loading || gamesLoading || !!error}
                            />
                        </>
                    }
                    options={
                        isMobileMenuOpen &&
                        !isFmdBreakpoint && (
                            <OptionWrapper>
                                {TimeSelect}
                                {DatePicker}
                                {GameSelect}
                            </OptionWrapper>
                        )
                    }>
                    <ChartWrapper padding="0">
                        <LineChart
                            selectedGames={selectedGames}
                            height={470}
                            data={chartData}
                            trends={trends}
                            limit={LEGENDS_LIMIT}
                            loading={loading || gamesLoading}
                            keysMap={keysMap}
                            emptyDataKeys={emptyDataKeys}
                            tooltipFormatter={() => formatDuration}
                            yTickFormatter={() => formatDuration}
                            xTickFormatter={xAxisFormatters[timeScale.value]}
                            tooltipLabelFormatter={tooltipFormatters[timeScale.value]}
                        />
                    </ChartWrapper>
                </Widget>
            </div>
        </PDFPictureTrap>
    );
};

export default withLocalization(AveragePlayTimeWidget);
